/**
 * Analytics Utility
 * Privacy-focused event tracking using Plausible Analytics
 *
 * Features:
 * - Page view tracking (automatic)
 * - Custom event tracking
 * - Outbound link tracking
 * - UTM parameter preservation
 * - localStorage-based attribution
 */

interface PlausibleWindow extends Window {
  plausible?: (eventName: string, options?: Record<string, unknown>) => void;
}

declare const window: PlausibleWindow;

/**
 * Initialize Plausible (called automatically on page load)
 * Plausible script in index.html handles this, but this provides fallback
 */
export const initAnalytics = () => {
  // Plausible is loaded via script tag in index.html
  // This function ensures initialization even if script is delayed
  if (typeof window !== 'undefined' && window.plausible) {
    console.debug('[Analytics] Plausible initialized');
  }
};

/**
 * Track a custom event
 *
 * @param name - Event name (e.g., 'demo_started', 'resource_clicked')
 * @param props - Optional event properties (max 30 properties per event)
 *
 * Example:
 * trackEvent('demo_started', { demo_type: 'cortex' })
 * trackEvent('resource_clicked', { resource_id: 'arch-doc', category: 'architecture' })
 */
export const trackEvent = (
  name: string,
  props?: Record<string, string | number | boolean>
) => {
  if (typeof window === 'undefined' || !window.plausible) {
    console.debug(`[Analytics] Event queued: ${name}`, props);
    return;
  }

  try {
    window.plausible(name, {
      props: props || {},
    });
    console.debug(`[Analytics] Event tracked: ${name}`, props);
  } catch (error) {
    console.error(`[Analytics] Failed to track event: ${name}`, error);
  }
};

/**
 * Track outbound link clicks
 * Includes 30ms delay to ensure tracking completes before navigation
 *
 * @param url - Destination URL
 * @param label - Human-readable label for the link
 * @param target - Optional window target ('_blank', etc.)
 *
 * Example:
 * trackOutboundLink('https://twitter.com/kempionresearch', 'Twitter follow')
 */
export const trackOutboundLink = (
  url: string,
  label: string,
  target: string = '_blank'
) => {
  if (typeof window === 'undefined') return;

  try {
    trackEvent('outbound_link', {
      url,
      label,
    });

    // Small delay to allow tracking to send before navigation
    setTimeout(() => {
      window.open(url, target);
    }, 30);
  } catch (error) {
    console.error('[Analytics] Failed to track outbound link', error);
    window.open(url, target);
  }
};

/**
 * Track form submission
 *
 * @param formName - Identifier for the form
 * @param props - Optional form metadata
 *
 * Example:
 * trackFormSubmission('contact', { interest: 'Research collaboration' })
 */
export const trackFormSubmission = (
  formName: string,
  props?: Record<string, string | number | boolean>
) => {
  trackEvent('form_submission', {
    form_name: formName,
    ...props,
  });
};

/**
 * Track demo or feature interactions
 *
 * @param demoName - Name of the demo/feature
 * @param action - Action taken (started, completed, closed)
 * @param props - Optional additional properties
 *
 * Example:
 * trackDemoInteraction('cortex_visualization', 'started')
 * trackDemoInteraction('chat_widget', 'message_sent', { message_length: 150 })
 */
export const trackDemoInteraction = (
  demoName: string,
  action: 'started' | 'completed' | 'closed' | 'error' | string,
  props?: Record<string, string | number | boolean>
) => {
  trackEvent(`demo_${action}`, {
    demo_name: demoName,
    ...props,
  });
};

/**
 * Track resource or content engagement
 *
 * @param resourceType - Type of resource (article, video, whitepaper, etc.)
 * @param resourceId - Unique identifier for the resource
 * @param category - Content category
 * @param props - Optional additional properties
 *
 * Example:
 * trackResourceClick('documentation', 'api-reference', 'technical')
 */
export const trackResourceClick = (
  resourceType: string,
  resourceId: string,
  category?: string,
  props?: Record<string, string | number | boolean>
) => {
  trackEvent('resource_clicked', {
    resource_type: resourceType,
    resource_id: resourceId,
    category: category || 'uncategorized',
    ...props,
  });
};

/**
 * Track email signup
 *
 * @param source - Where the signup originated
 * @param props - Optional additional properties
 *
 * Example:
 * trackEmailSignup('contact_form')
 * trackEmailSignup('chat_widget', { conversation_id: 'abc123' })
 */
export const trackEmailSignup = (
  source: string,
  props?: Record<string, string | number | boolean>
) => {
  trackEvent('email_signup', {
    signup_source: source,
    ...props,
  });
};

/**
 * Track social share clicks
 *
 * @param platform - Social platform (twitter, hackernews, linkedin, etc.)
 * @param props - Optional additional properties
 *
 * Example:
 * trackSocialShare('twitter', { page: 'home' })
 */
export const trackSocialShare = (
  platform: string,
  props?: Record<string, string | number | boolean>
) => {
  trackEvent('social_share', {
    platform,
    timestamp: new Date().toISOString(),
    ...props,
  });
};

/**
 * ===== UTM Parameter Management =====
 * Preserve UTM parameters across navigation for attribution
 */

const UTM_STORAGE_KEY = 'kempion_utm_params';
const UTM_PARAM_NAMES = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/**
 * Extract and store UTM parameters from URL
 * Called automatically on page load
 */
export const captureUTMParams = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmData: Record<string, string> = {};

  UTM_PARAM_NAMES.forEach((param) => {
    const value = params.get(param);
    if (value) {
      utmData[param] = value;
    }
  });

  if (Object.keys(utmData).length > 0) {
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
      console.debug('[Analytics] UTM params captured:', utmData);

      // Track that user came from campaign
      trackEvent('utm_source_detected', utmData);
    } catch (error) {
      console.error('[Analytics] Failed to store UTM params', error);
    }
  }
};

/**
 * Get stored UTM parameters
 * Returns object with utm_source, utm_medium, etc.
 *
 * Example:
 * const utm = getUTMParams();
 * console.log(utm.utm_source); // 'twitter'
 */
export const getUTMParams = (): Record<string, string | null> => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Analytics] Failed to retrieve UTM params', error);
    return {};
  }
};

/**
 * Clear stored UTM parameters
 */
export const clearUTMParams = () => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
    console.debug('[Analytics] UTM params cleared');
  } catch (error) {
    console.error('[Analytics] Failed to clear UTM params', error);
  }
};

/**
 * Get UTM source for analytics
 * Returns the utm_source if available, otherwise tries to infer from referrer
 */
export const getAttributionSource = (): string => {
  const utm = getUTMParams();
  if (utm.utm_source) {
    return utm.utm_source as string;
  }

  // Fallback to referrer domain
  if (typeof window !== 'undefined' && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      const hostname = referrerUrl.hostname.replace('www.', '');

      if (hostname.includes('twitter')) return 'twitter';
      if (hostname.includes('hackernews') || hostname.includes('news.ycombinator')) return 'hackernews';
      if (hostname.includes('linkedin')) return 'linkedin';
      if (hostname.includes('reddit')) return 'reddit';

      return hostname;
    } catch {
      return 'direct';
    }
  }

  return 'direct';
};

/**
 * Track page view with attribution
 * Called automatically by Plausible, but can be used for custom pages
 *
 * @param pageName - Name of the page
 */
export const trackPageView = (pageName: string) => {
  const attribution = getAttributionSource();
  trackEvent('page_view', {
    page_name: pageName,
    attribution_source: attribution,
  });
};

/**
 * Setup analytics on app initialization
 * Call this once on app startup (in App.tsx or main.tsx)
 */
export const setupAnalytics = () => {
  if (typeof window === 'undefined') return;

  // Capture UTM parameters if present
  captureUTMParams();

  // Initialize Plausible
  initAnalytics();

  console.debug('[Analytics] Setup complete');
};
